import { aheadWords } from './business-settings'
import { askAgainLater } from './followup'
import { shelved } from './planning'
import { replyForJob, replyGroup, replyQuiet, secondMessageFrom, stageOf } from './rebook-message'
import type { Customer } from './types'
import { addDaysISO, dayLabel, shortDate } from './ui'
import { standingWords } from './visit-words'

// After Book a visit saves a NEW visit that is not agreed (Liam approved the design, 9 Oct 2026: canvas artboard Then;
// rules FIXES-20261008/LIAM-book-a-visit-design.md): whether the saved bar offers "Message them about <day>", and when
// it does not, the one line that says why. Never the Ask pop-up: Messages asks, in its own card, with its own rules.
// Pure, so the checks read the rule the bar draws.
//
// Offered only when Messages would have a first message for THIS visit's job ready to send, asking about this day:
// inside How far ahead to ask, a customer on the round who is messaged, not left alone by Where they stand, Ask again
// later or a reply (the job's own conversation: windows waiting never quietens a new gutters visit), not messaged yet
// this time round, and no other visit of that job on the diary for Messages to ask about first.

export interface MessageThemInput {
  customer: Customer
  // The job's own messaging record: the customer for job 1, the extra job's row for one of theirs.
  conversation: { rebook_messages?: number | null; rebook_messaged_on?: string | null; last_clean?: string | null }
  jobId: number | null
  date: string
  today: string
  aheadDays: number
  agreed: boolean
  weekOnly: boolean
  way: string
  otherVisit: string | null // another visit of the same job on the diary from today on
  // Put on the day just now in the Diary (Liam, 11 Oct 2026): a message from before, once its wait is over, was not
  // about this day, so this day is asked about (counted as Messages counts a fresh ask).
  placed?: boolean
  // A visit that couldn't be done, just moved to its new day (docs/plans/rearrange/PLAN.md): telling them is always due,
  // whatever Messages was waiting on, so only Don't message, not on the round and days offered still waiting stop it.
  missed?: boolean
}

export type MessageThemOffer = { offer: true } | { offer: false; why: string | null }

export function messageThemOffer(i: MessageThemInput): MessageThemOffer {
  const no = (why: string | null): MessageThemOffer => ({ offer: false, why })
  // Agreed, or no day chosen yet: there is nothing to ask about a day.
  if (i.agreed || i.weekOnly || i.date < i.today) return no(null)
  if (i.way === 'none') return no("They're set to Don't message.")
  const c = i.customer
  if (c.status !== 'active' || shelved(c) || c.customer_list) return no(`Messages leaves them be: ${standingWords(c) || 'not on the round'}.`)
  if (!i.missed && askAgainLater(c, i.today)) return no(`Messages asks them again from ${shortDate(c.follow_up_on)}.`)
  if (replyQuiet(c, i.today, i.jobId)) {
    const group = replyGroup(replyForJob(c, i.jobId), i.today)
    if (group === 'datesoffered' || !i.missed) return no(group === 'datesoffered' ? 'Messages is waiting for them to pick one of the days you offered.' : group === 'replybooked' ? 'They have already picked a day in Messages.' : 'Their reply is filed in Messages, so there is nothing to send.')
  }
  if (i.missed) return { offer: true }
  const stage = stageOf(i.conversation, i.today)
  if (stage === 'waiting') return no('Messages is waiting for their reply to your last message.')
  if (stage !== 'to_message') {
    // Put on a day in the Diary: asked about it once the wait after their last message is over (a second message sent
    // a minute ago is never followed by another: round 5 review).
    const from = secondMessageFrom(i.conversation)
    if (!i.placed) return no('Their next message in Messages is the second one, which asks about no day.')
    if (from && i.today < from) return no('Messages is waiting for their reply to your last message.')
  }
  if (i.otherVisit) return no(`They also have ${dayLabel(i.otherVisit)} on the diary, so Messages asks about that one.`)
  if (i.date > addDaysISO(i.today, i.aheadDays)) return no(`Messages lists them from ${shortDate(addDaysISO(i.date, -i.aheadDays))} (How far ahead to ask: ${aheadWords(i.aheadDays)}).`)
  return { offer: true }
}
